import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Loader from '@repo/ui/loader'
import { useQuery } from '@tanstack/react-query'

import { getCenterBudget } from '../../../../api/centers/get-center-budget'
import { getCenterCostsView } from '../../../../api/centers/get-center-costs-view'
import useTranslate from '../../../../lib/hooks/use-translate'
import { ActivityMemberEnum } from '../../../../lib/types/enums/activity-member/activity-member.enum'
import { CurrencyEnum } from '../../../../lib/types/enums/common/currency.enum'
import PaymentDistribution from './payment-distribution'

type Props = {
  centerId: number
  currency?: CurrencyEnum
  mode: 'open' | 'validate' | 'view' | 'staff-view'
}

const CenterAdminAndGrandExpenses = ({ centerId, currency, mode }: Props) => {
  const { t } = useTranslate()

  const isStaffView = mode === 'staff-view'

  const { data: budget } = useQuery({
    queryKey: ['center-budgets'],
    queryFn: () => getCenterBudget(centerId)
  })

  const { data: centerCost } = useQuery({
    queryKey: ['center-costs-view', centerId],
    queryFn: () => getCenterCostsView(centerId)
  })

  if (!budget || !centerCost) return <Loader center />

  const isAdminPaidByLocalOrganizer =
    centerCost.localTransportAndAdminPaidBy === ActivityMemberEnum.ORGANIZER

  return (
    <>
      <PaymentDistribution
        title={t('ol.center.admin.expenses.title')}
        total={{
          label: t('ol.center.admin.expenses.total'),
          amount: budget.totalAdminExpenses,
          variant: 'warning',
          isBadge: true
        }}
        amounts={[
          {
            label: isAdminPaidByLocalOrganizer
              ? t('ol.center.admin.expenses.paid.by.local.organizer')
              : t('ol.center.admin.expenses.paid.by.french.leader'),
            amount: budget.totalAdminExpenses,
            isBadge: isStaffView,
            bgColor: tailwindColors.secondary.light.DEFAULT,
            textColor: tailwindColors.secondary.DEFAULT
          }
        ]}
        currency={currency}
        hideTotal={isStaffView}
      />
      <div className="my-5 border-t border-dashed border-grey-stroke w-full" />
      <PaymentDistribution
        title={t('ol.center.grand.expenses.title')}
        className="bg-secondary-light border border-secondary-light-hover "
        total={{
          label: t('ol.center.grand.expenses.total'),
          amount: budget.totalBudget,
          isBadge: true,
          bgColor: tailwindColors.secondary.DEFAULT,
          textColor: tailwindColors.white.DEFAULT
        }}
        hideTotal={isStaffView}
        amounts={[
          {
            label: t('ol.center.grand.expenses.paid.by.local.organizer'),
            amount: centerCost.grandTotalLocalOrganizerCost,
            isBadge: true,
            bgColor: tailwindColors.secondary.light.hover,
            textColor: tailwindColors.secondary.DEFAULT,
            hideItem: isStaffView
          },
          {
            label: t('ol.center.grand.expenses.paid.by.french.leader'),
            amount: centerCost.grandTotalFrenchLeaderCost,
            isBadge: true,
            bgColor: isStaffView
              ? tailwindColors.secondary.DEFAULT
              : tailwindColors.secondary.light.hover,
            textColor: isStaffView
              ? tailwindColors.secondary.light.DEFAULT
              : tailwindColors.secondary.DEFAULT
          }
        ]}
        currency={currency}
      />
    </>
  )
}

export default CenterAdminAndGrandExpenses
